import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { importDraftsApi, type ImportDraftStatus, type ImportDraftView } from "../api/structure";
import type { ApiError } from "../api/client";
import { formatDateTime } from "../lib/dates";
import { usePrompt } from "../components/ConfirmDialog";
import { FileDropZone } from "../components/FileDropZone";
import { PermLink } from "../components/PermLink";
import { useToast } from "../components/Toast";
import Ic from "../components/icons.jsx";
import { importErrorText } from "./structure/importErrors";

type Filter = ImportDraftStatus | "ALL";

/**
 * `/struktura/uvoz` — every import of a CAD bill of materials (sprint 5j §4.1,
 * 2026-10-20), from an .xlsx or a .csv. A file dropped here becomes a draft at once and opens; drafts
 * wait — several at a time — until someone finishes them, imported ones stay as
 * history with a link to the machine they made. `?parentId=` (the button on
 * Struktura) gives a new draft that position as the place where it goes.
 */
export default function StructureImportListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const prompt = usePrompt();
  const presetParentId = useSearchParams()[0].get("parentId");
  const fileInput = useRef<HTMLInputElement>(null);

  const [drafts, setDrafts] = useState<ImportDraftView[] | null>(null);
  const [filter, setFilter] = useState<Filter>("DRAFT");
  const [reloadKey, setReloadKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    let cancelled = false;
    importDraftsApi.list()
      .then((list) => { if (!cancelled) setDrafts(list); })
      .catch((err: ApiError) => { if (!cancelled) { setDrafts([]); setError(err); } });
    return () => { cancelled = true; };
  }, [reloadKey]);

  async function upload(file: File) {
    setError(null);
    setUploading(true);
    try {
      const draft = await importDraftsApi.create(file, presetParentId);
      navigate(`/struktura/uvoz/${draft.id}`);
    } catch (err) {
      setError(err as ApiError);
      setUploading(false);
    }
  }

  async function remove(draft: ImportDraftView) {
    const reason = await prompt({
      title: t("structureImport.deleteTitle", "Brisanje nacrta"),
      message: t("structureImport.deleteMessage", "Obrisati nacrt „{{name}}“? U strukturi se ništa ne menja.", { name: draft.name }),
      confirmLabel: t("action.delete", "Obriši"),
      placeholder: t("deleteReason.placeholder", "Razlog brisanja (obavezno) — npr. rashodovano, zamenjeno, greška pri unosu…"),
      multiline: true,
      required: true,
      danger: true,
    });
    if (!reason) return;
    try {
      await importDraftsApi.remove(draft.id, reason);
      toast.success(t("structureImport.deleted", "Nacrt „{{name}}“ je obrisan.", { name: draft.name }));
      setReloadKey((k) => k + 1);
    } catch (err) {
      setError(err as ApiError);
    }
  }

  const draftCount = drafts?.filter((d) => d.status === "DRAFT").length ?? 0;
  const importedCount = drafts?.filter((d) => d.status === "IMPORTED").length ?? 0;
  const shown = drafts?.filter((d) => filter === "ALL" || d.status === filter) ?? null;

  function whereText(d: ImportDraftView) {
    if (d.toTopLevel) return <span>{t("structureImport.whereTop", "Prvi nivo")}</span>;
    if (d.targetName) return <span>{d.targetName}</span>;
    if (d.targetMissing) return <span className="is-err-text">{t("structureImport.whereMissing", "pozicija više ne postoji")}</span>;
    return <span className="muted">{t("structureImport.whereNone", "nije izabrano")}</span>;
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("structureImport.title", "Uvoz strukture iz Excela")}</h1>
          <div className="sub">{t("structureImport.subtitle", "Svaki ubačen fajl postaje nacrt: pregledaš ga kad stigneš — danas, sutra ili prekosutra — pa ga ubaciš u strukturu.")}</div>
        </div>
        <div className="page-actions">
          <Link className="btn" to="/struktura" style={{ textDecoration: "none" }}><Ic.tree /> {t("structureImport.back", "Nazad na strukturu")}</Link>
          <button type="button" className="btn primary" onClick={() => fileInput.current?.click()} disabled={uploading}>
            <Ic.upload /> {uploading ? t("structureImport.reading", "Čitam fajl…") : t("structureImport.new", "Nov uvoz")}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.csv"
            hidden
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void upload(f); }}
          />
        </div>
      </div>

      {error && <div style={errorBox} role="alert">{importErrorText(t, error)}</div>}

      <FileDropZone onFiles={(files) => { void upload(files[0]); }} accept=".xlsx,.csv" multiple={false} disabled={uploading}>
        <section className="card">
          <div className="card-h" style={{ flexWrap: "wrap", gap: 8 }}>
            <div className="chip-group" role="group" aria-label={t("structureImport.filterAria", "Koji uvozi")}>
              <button type="button" className={`chip${filter === "DRAFT" ? " active" : ""}`} aria-pressed={filter === "DRAFT"} onClick={() => setFilter("DRAFT")}>
                {t("structureImport.filter.drafts", "Nacrti")} <span className="chip-n">{draftCount}</span>
              </button>
              <button type="button" className={`chip${filter === "IMPORTED" ? " active" : ""}`} aria-pressed={filter === "IMPORTED"} onClick={() => setFilter("IMPORTED")}>
                {t("structureImport.filter.imported", "Uvezeni")} <span className="chip-n">{importedCount}</span>
              </button>
              <button type="button" className={`chip${filter === "ALL" ? " active" : ""}`} aria-pressed={filter === "ALL"} onClick={() => setFilter("ALL")}>
                {t("structureImport.filter.all", "Svi")}
              </button>
            </div>
            <small className="muted">{t("structureImport.dropHintShort", "Excel (.xlsx) ili CSV iz CAD-a, do 5 MB — prevuci ovde ili klikni „Nov uvoz“.")}</small>
          </div>
          {shown === null ? (
            <div className="empty">{t("common.loading", "Učitavanje…")}</div>
          ) : shown.length === 0 ? (
            <div className="empty">
              {filter === "IMPORTED"
                ? t("structureImport.emptyImported", "Još ništa nije uvezeno.")
                : t("structureImport.empty", "Nema nacrta uvoza. Ubaci Excel fajl da počneš.")}
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl list-card">
                <thead>
                  <tr>
                    <th>{t("structureImport.col.name", "Nacrt")}</th>
                    <th>{t("structureImport.col.status", "Status")}</th>
                    <th className="num">{t("structureImport.col.selected", "Označeno")}</th>
                    <th className="num">{t("structureImport.col.positions", "Pozicija")}</th>
                    <th className="hide-tablet">{t("structureImport.col.where", "Gde ide")}</th>
                    <th>{t("structureImport.col.changed", "Poslednja izmena")}</th>
                    <th aria-label={t("structureImport.col.actions", "Akcije")} />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((d) => (
                    <tr key={d.id} className="is-clickable" onClick={() => navigate(`/struktura/uvoz/${d.id}`)}>
                      <td data-label={t("structureImport.col.name", "Nacrt")}>
                        <Link to={`/struktura/uvoz/${d.id}`} onClick={(e) => e.stopPropagation()}><b>{d.name}</b></Link>
                        <div className="muted import-list-file">{d.fileName}</div>
                      </td>
                      <td data-label={t("structureImport.col.status", "Status")}>
                        {d.status === "IMPORTED"
                          ? <span className="tag ok">{t("structureImport.status.imported", "Uvezen")}</span>
                          : <span className="tag info">{t("structureImport.status.draft", "Nacrt")}</span>}
                      </td>
                      <td className="num" data-label={t("structureImport.col.selected", "Označeno")}>
                        {t("structureImport.selectedOf", "{{selected}} od {{items}}", { selected: d.selectedCount, items: d.itemCount })}
                        {d.status === "DRAFT" && d.incompleteCount > 0 && (
                          <div className="is-err-text import-list-file">{t("structureImport.incompleteShort", "{{count}} bez vrste ili naziva", { count: d.incompleteCount })}</div>
                        )}
                      </td>
                      <td className="num" data-label={t("structureImport.col.positions", "Pozicija")}>
                        {d.status === "IMPORTED" ? d.importedCount : d.positionCount}
                      </td>
                      <td className="hide-tablet" data-label={t("structureImport.col.where", "Gde ide")}>{whereText(d)}</td>
                      <td data-label={t("structureImport.col.changed", "Poslednja izmena")} className="muted" style={{ fontSize: 12.5 }}>
                        {d.status === "IMPORTED"
                          ? t("structureImport.importedBy", "uvezeno {{when}} · {{who}}", { when: formatDateTime(d.importedAt), who: d.importedByName ?? "—" })
                          : `${formatDateTime(d.updatedAt)} · ${d.updatedByName ?? d.createdByName ?? "—"}`}
                      </td>
                      <td data-label={t("structureImport.col.actions", "Akcije")} className="import-list-actions">
                        {d.status === "IMPORTED" ? (
                          d.importRootId && (
                            <PermLink permission="structure:view" className="btn sm" to={`/struktura/${d.importRootId}`} style={{ textDecoration: "none" }} onClick={(e) => e.stopPropagation()}>
                              <Ic.tree /> {t("structureImport.showInStructure", "Prikaži u strukturi")}
                            </PermLink>
                          )
                        ) : (
                          <button type="button" className="btn sm" style={dangerBtn} onClick={(e) => { e.stopPropagation(); void remove(d); }}>
                            {t("action.delete", "Obriši")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </FileDropZone>
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
